import { useMemo } from 'react';

import { useGetFile } from '@/hooks/useFiles';
import type {
  EnrichedVulnerability,
  FrameworkRef,
} from '@/sections/threats/types/enrichment.types';
import type { RiskFrameworkEnrichment } from '@/sections/vulnerabilities/types';
import {
  parseRiskFrameworkEnrichment,
  riskEnrichmentToFrameworkRefs,
  riskFrameworkS3Key,
} from '@/sections/vulnerabilities/utils/frameworkEnrichment.util';

/**
 * Resolves the CVE id for a risk using the SAME predicate as
 * VulnerabilityDrawer (RiskDrawerContent, VulnerabilityDrawer.tsx): the
 * risk's own name first, then the attached vulnerability's id.
 */
export function riskCveId(
  riskName: string,
  vulnerabilityId?: string
): string | undefined {
  return riskName?.startsWith('CVE-')
    ? riskName
    : vulnerabilityId?.startsWith('CVE-')
      ? vulnerabilityId
      : undefined;
}

/**
 * Framework references for a risk, from the same two sources — with the same
 * precedence — as the drawer's Frameworks tab:
 *
 *  1. `enrichments/cve/<CVE>.json` (global CVE enrichment, `framework_refs`),
 *     when the risk resolves to a CVE. Backend derives these with the full
 *     framework context (ATT&CK/D3FEND bridges), so they are the richer set.
 *  2. `enrichments/risk-frameworks/<risk name>.json` (per-account file the
 *     backend writes only when the risk has CWE codes).
 *
 * Both fetches share react-query cache entries with the drawer (identical
 * query keys), so opening the drawer after the table costs no extra fetch.
 */
export function useRiskFrameworkRefs(
  riskName: string,
  vulnerabilityId?: string
): FrameworkRef[] {
  const s3Key = riskName ? riskFrameworkS3Key(riskName) : '';
  const { data: riskEnrichmentRaw } = useGetFile<RiskFrameworkEnrichment>(
    { name: s3Key },
    { enabled: !!s3Key }
  );

  const cveId = riskCveId(riskName, vulnerabilityId);
  const { data: threatIntel } = useGetFile<EnrichedVulnerability>(
    { name: `enrichments/cve/${cveId}.json`, global: true },
    { enabled: !!cveId }
  );

  return useMemo(() => {
    const enrichment = parseRiskFrameworkEnrichment(riskEnrichmentRaw);
    const riskFileRefs = enrichment
      ? riskEnrichmentToFrameworkRefs(enrichment)
      : [];
    // Mirrors VulnerabilityDrawer: `threatIntel?.framework_refs ?? frameworkRefs`.
    // `framework_refs` is json:omitempty on the backend, so it is either absent
    // (→ fall back) or a non-empty array. A 404 yields `''` from useGetFile,
    // and `''?.framework_refs` is undefined → fall back.
    return threatIntel?.framework_refs ?? riskFileRefs;
  }, [threatIntel, riskEnrichmentRaw]);
}
